import type React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { checkIsTariffApplied, getTariffBreakDownDetails } from '../../../services/search-page/search-page-service';
import 'react-modern-drawer/dist/index.css';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import images from 'assets/images';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import Slider from 'react-slick';
import RateCard from '../RateCard';
import type { IConnectorDetail, ITariffComponent } from '../../../models/search-page';
import type { ITariffDetailsProps } from '../../../types/propTypes';

export const useTariffDetails = (props: ITariffDetailsProps) => {
    const { showModal, onHide, connectorId, details } = props;
    const navigate = useNavigate();
    const { t } = useTranslation();

    const [holding, setHolding] = useState<boolean>(false);
    const [drawerOpen, setDrawerOpen] = useState<boolean>(true);

    const [currentSlide, setCurrentSlide] = useState(0);
    const sliderRef = useRef<Slider | null>(null);

    const [tariffComponent, setTariffComponent] = useState<ITariffComponent[]>([]);
    const [connectorDetails, setConnectorDetails] = useState<IConnectorDetail | null>(null);

    const [showTariffBreakdownModal, setShowTariffBreakdownModal] = useState<boolean>(false);
    const [showTooltip, setShowTooltip] = useState<boolean>(false);

    const isDesktop = useMediaQuery({ query: `(min-width: 768px)` });

    const hasFetched = useRef(false);

    useEffect(() => {
        if (!hasFetched.current) {
            getTariffBreakdownData();
            hasFetched.current = true;
        }
    }, []);

    const slides = useMemo(() => {
        let tooltipAssigned = false;
        const flattened = tariffComponent.flatMap((item) =>
            item.price_breakdown
                .filter((p) => p.position === 1)
                .map((p) => {
                    const qualifies = item.price_breakdown.length > 1;
                    const isShowTooltip = !tooltipAssigned && qualifies;

                    if (isShowTooltip) {
                        tooltipAssigned = true;
                    }

                    return {
                        ...p,
                        type_label: item.type_label,
                        unit: item.unit,
                        image: item.image,
                        isShowTooltip: isShowTooltip,
                        otherPrices: item.price_breakdown.filter((other) => other.position !== 1),
                        priceBreakdown: item.price_breakdown,
                    };
                }),
        );

        const elements: React.ReactElement[] = [];

        flattened.forEach((item, index) => {
            elements.push(
                <div
                    key={`card-${index}`}
                    style={{ minWidth: 120 }}
                    className={index < flattened.length - 1 ? `with-divider ${holding ? 'plus-hold' : ' '}` : ''}
                >
                    <div className="ratecard-wrapper">
                        <RateCard
                            {...item}
                            holding={holding}
                            showTariffBreakdown={showTariffBreakdownModal}
                            setShowTariffBreakdown={setShowTariffBreakdownModal}
                            showTooltip={showTooltip && item.isShowTooltip && currentSlide == index}
                            setShowTooltip={setShowTooltip}
                            cardIndex={index}
                            currentSlide={currentSlide}
                        />
                    </div>
                </div>,
            );
        });
        return elements;
    }, [tariffComponent, holding, showTooltip]);

    const settings = useMemo(
        () => ({
            dots: false,
            infinite: false,
            slidesToShow: 1,
            speed: 500,
            slidesToScroll: 1,
            variableWidth: true,
            mobileFirst: true,
            arrows: false,
            afterChange: (index: number) => {
                setCurrentSlide(index);
                setShowTooltip(false);
            },
            draggable: !showTariffBreakdownModal,
            swipe: !showTariffBreakdownModal,
            touchMove: !showTariffBreakdownModal,
        }),
        [showTariffBreakdownModal],
    );

    const getTariffBreakdownData = async () => {
        await getTariffBreakDownDetails(connectorId)
            .then((res) => {
                if (res.Data && res.Data.active_components) {
                    setTariffComponent(res.Data.active_components);
                    setConnectorDetails(res.Data.connector_details);
                }
            })
            .catch((m) => {
                if (m.response && m.response.data && m.response.data.message) {
                    toast.error(m.response.data.message);
                } else {
                    toast.error(m.toString());
                }
            });
    };

    const startHold = () => {
        setHolding(true);
        setShowTooltip(false);
    };

    const stopHold = () => {
        setHolding(false);
    };

    const checkTariffAvailability = async () => {
        await checkIsTariffApplied(connectorId)
            .then((res) => {
                if (res) {
                    navigate(`start-charge/${connectorId}`, {
                        state: details,
                    });
                }
            })
            .catch((m) => {
                if (m.response && m.response.data && m.response.data.message) {
                    toast.error(m.response.data.message);
                } else {
                    toast.error(m.toString());
                }
            })
            .finally(() => {
                stopHold();
            });
    };

    const renderComponent = () => {
        const totalSlides = slides.length;

        return (
            <div className={`tariff-details ${holding ? 'blurred' : ''}`}>
                <div className="d-flex justify-content-between mb-3">
                    {currentSlide > 0 ? (
                        <img
                            src={images.arrowLeftSVG}
                            style={{ height: '20px', width: '20px' }}
                            className="charging-icon"
                            alt="tariff-component-icon"
                            onClick={() => sliderRef.current?.slickPrev()}
                        />
                    ) : (
                        <div style={{ height: '20px', width: '20px' }}></div>
                    )}
                    <div
                        className="text-center flex-grow-1"
                        style={{
                            fontSize: '15px',
                            color: '#404040',
                            fontWeight: 'bold',
                        }}
                    >
                        {t<string>('stationDetails.tariffDetailsModal.currentRates')}
                    </div>
                    {currentSlide < totalSlides - 1 ? (
                        <img
                            src={images.arrowRightSVG}
                            style={{ height: '20px', width: '20px' }}
                            className="charging-icon"
                            alt="tariff-component-icon"
                            onClick={() => sliderRef.current?.slickNext()}
                        />
                    ) : (
                        <div style={{ height: '20px', width: '20px' }}></div>
                    )}
                </div>

                <Slider ref={sliderRef} {...settings}>
                    {slides}
                </Slider>
            </div>
        );
    };

    return {
        t,
        showModal,
        connectorId,
        details,
        holding,
        drawerOpen,
        currentSlide,
        tariffComponent,
        connectorDetails,
        showTariffBreakdownModal,
        showTooltip,
        isDesktop,
        setCurrentSlide,
        setDrawerOpen,
        onHide,
        getTariffBreakdownData,
        startHold,
        stopHold,
        checkTariffAvailability,
        renderComponent,
    };
};
